"use client"

import * as React from "react"

import { usePersistedState } from "@exxatdesignux/ui/lib/persisted-state"
import {
  DEFAULT_EXAM_CATEGORIES,
  EXAM_CATEGORIES_STORE_KEY,
  applyCanonicalDomainNames,
  buildCategoryTree,
  findCategory,
  getChildCategories,
  getDescendantCategoryIds,
  getRootCategories,
  type ExamCategory,
  type ExamCategoryTreeNode,
} from "@/lib/exam-categories"

export function useExamCategories() {
  const [categories, setCategories] = usePersistedState<ExamCategory[]>(
    EXAM_CATEGORIES_STORE_KEY,
    DEFAULT_EXAM_CATEGORIES.map((category) => ({ ...category })),
    { debounceMs: 0 },
  )

  React.useEffect(() => {
    setCategories((current) => {
      const next = applyCanonicalDomainNames(current)
      const changed = next.some(
        (category, index) => category.name !== current[index]?.name,
      )
      return changed ? next : current
    })
  }, [setCategories])

  const tree = React.useMemo<ExamCategoryTreeNode[]>(
    () => buildCategoryTree(categories),
    [categories],
  )

  const roots = React.useMemo(() => getRootCategories(categories), [categories])

  const upsertCategory = React.useCallback((next: ExamCategory) => {
    setCategories((current) => {
      const index = current.findIndex((category) => category.id === next.id)
      if (index === -1) return [...current, next]
      const copy = [...current]
      copy[index] = next
      return copy
    })
  }, [setCategories])

  const addCategory = React.useCallback(
    (input: Omit<ExamCategory, "id" | "sortOrder"> & { id?: string }) => {
      const siblings = input.parentId
        ? getChildCategories(categories, input.parentId)
        : getRootCategories(categories)
      const next: ExamCategory = {
        id: input.id ?? `cat-${Date.now()}`,
        name: input.name.trim(),
        parentId: input.parentId,
        description: input.description?.trim() || undefined,
        sortOrder: siblings.length,
        isActive: input.isActive ?? true,
      }
      upsertCategory(next)
      return next
    },
    [categories, upsertCategory],
  )

  const updateCategory = React.useCallback(
    (categoryId: string, patch: Partial<Omit<ExamCategory, "id">>) => {
      setCategories((current) =>
        current.map((category) =>
          category.id === categoryId ? { ...category, ...patch } : category,
        ),
      )
    },
    [setCategories],
  )

  const removeCategory = React.useCallback(
    (categoryId: string) => {
      setCategories((current) => {
        const descendantIds = getDescendantCategoryIds(current, categoryId)
        const removeIds = new Set([categoryId, ...descendantIds])
        return current.filter((category) => !removeIds.has(category.id))
      })
    },
    [setCategories],
  )

  const moveCategory = React.useCallback(
    (categoryId: string, direction: "up" | "down") => {
      setCategories((current) => {
        const node = current.find((category) => category.id === categoryId)
        if (!node) return current
        const siblings = current
          .filter((category) => category.parentId === node.parentId)
          .sort((a, b) => a.sortOrder - b.sortOrder)
        const index = siblings.findIndex((category) => category.id === categoryId)
        const swapIndex = direction === "up" ? index - 1 : index + 1
        if (swapIndex < 0 || swapIndex >= siblings.length) return current
        const swapNode = siblings[swapIndex]!
        return current.map((category) => {
          if (category.id === node.id) return { ...category, sortOrder: swapNode.sortOrder }
          if (category.id === swapNode.id) return { ...category, sortOrder: node.sortOrder }
          return category
        })
      })
    },
    [setCategories],
  )

  const resetToDefaults = React.useCallback(() => {
    setCategories(DEFAULT_EXAM_CATEGORIES.map((category) => ({ ...category })))
  }, [setCategories])

  const moveCategoryBefore = React.useCallback(
    (activeId: string, overId: string) => {
      setCategories((current) => {
        const active = findCategory(current, activeId)
        const over = findCategory(current, overId)
        if (!active || !over || activeId === overId) return current
        const descendants = getDescendantCategoryIds(current, activeId)
        if (descendants.includes(overId)) return current

        const targetParentId = over.parentId
        const nextSiblings = current
          .filter(
            (category) =>
              category.parentId === targetParentId && category.id !== activeId,
          )
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
        const insertIndex = nextSiblings.findIndex((category) => category.id === overId)
        if (insertIndex < 0) return current

        nextSiblings.splice(insertIndex, 0, { ...active, parentId: targetParentId })
        const sortOrderMap = new Map(
          nextSiblings.map((category, index) => [category.id, index]),
        )

        const oldParentId = active.parentId
        const oldSiblings = current
          .filter(
            (category) =>
              category.parentId === oldParentId && category.id !== activeId,
          )
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))

        return current.map((category) => {
          if (sortOrderMap.has(category.id)) {
            return {
              ...category,
              parentId: targetParentId,
              sortOrder: sortOrderMap.get(category.id)!,
            }
          }
          if (category.parentId === oldParentId && category.id !== activeId) {
            const index = oldSiblings.findIndex((item) => item.id === category.id)
            if (index >= 0) return { ...category, sortOrder: index }
          }
          return category
        })
      })
    },
    [setCategories],
  )

  const reparentCategory = React.useCallback(
    (categoryId: string, newParentId: string) => {
      setCategories((current) => {
        const active = findCategory(current, categoryId)
        if (!active || categoryId === newParentId) return current
        const descendants = getDescendantCategoryIds(current, categoryId)
        if (descendants.includes(newParentId)) return current

        const oldParentId = active.parentId
        const oldSiblings = getChildCategories(current, oldParentId).filter(
          (category) => category.id !== categoryId,
        )
        const nextOrder = getChildCategories(current, newParentId).filter(
          (category) => category.id !== categoryId,
        ).length

        return current.map((category) => {
          if (category.id === categoryId) {
            return { ...category, parentId: newParentId, sortOrder: nextOrder }
          }
          if (category.parentId === oldParentId && category.id !== categoryId) {
            const index = oldSiblings.findIndex((item) => item.id === category.id)
            if (index >= 0) return { ...category, sortOrder: index }
          }
          return category
        })
      })
    },
    [setCategories],
  )

  const replaceCategories = React.useCallback(
    (next: ExamCategory[]) => {
      setCategories(next.map((category) => ({ ...category })))
    },
    [setCategories],
  )

  return {
    categories,
    tree,
    roots,
    upsertCategory,
    addCategory,
    updateCategory,
    removeCategory,
    moveCategory,
    moveCategoryBefore,
    reparentCategory,
    resetToDefaults,
    replaceCategories,
  }
}
